import React, { useId } from 'react';
type VolumeCoverProps = {
  number: number;
  /** Title split into the lines it prints on (max two). The last line prints in highlighter. */
  lines: string[];
  /** Event-group colour for the top band and the athletes, or 'team' for all groups. */
  color: string;
  sessions: number;
  /** Back cover in a stack: shows the volume name as a tab in the top strip. */
  peek?: boolean;
  className?: string;
  style?: React.CSSProperties;
};
const CHALK = '#F5F1E8';
const CLAY = '#C8452B';
const HL = '#F2C230';
const NAVY = '#14213D';
const OSWALD = "Oswald, 'Arial Narrow', sans-serif";
/** Sprints/Hurdles, Jumps, Throws, Distance */
const GROUPS = ['#C8452B', '#E89B0C', '#2E7D4F', '#2563A8'];

/**
 * Code-drawn landscape cover (16:9) in the volume look: Track Navy, a clay
 * oval seen from above, athletes as dots in the group colour, the event-group
 * band at the top, highlighter badges and the chalk band.
 */
export function VolumeCover({
  number,
  lines,
  color,
  sessions,
  peek = false,
  className = '',
  style
}: VolumeCoverProps) {
  const uid = useId().replace(/:/g, '');
  const label = `Track Coach Ready, Volume ${number}: ${lines.join(' ')}`;
  const lanes = [0, 1, 2, 3, 4];
  const isTeam = color === 'team';
  const dotColor = (i: number) => isTeam ? GROUPS[i % GROUPS.length] : color;
  const cardColor = isTeam ? CLAY : color;
  return <div role="img" aria-label={label} style={style} className={`cover-navy relative aspect-[16/9] overflow-hidden rounded-[6px] shadow-book ring-1 ring-black/40 ${className}`} data-id="element-564" data-mp="3ccpzhrdkdcp4">
      <svg viewBox="0 0 160 90" className="absolute inset-0 h-full w-full" aria-hidden="true" data-id="element-565" data-mp="32izg30wansga">
        <defs data-id="element-566" data-mp="2ygw2a5m5kbru">
          <clipPath id={`c${uid}`} data-id="element-567" data-mp="2tfybx5qzobf9">
            <rect x="0" y="0" width="160" height="82" data-id="element-568" data-mp="2tgzwc0twaa9n" />
          </clipPath>
        </defs>

        {/* the oval, seen from above */}
        <g clipPath={`url(#c${uid})`} data-id="element-569" data-mp="wmwpfftdtmd1">
          <rect x="84" y="14" width="118" height="78" rx="39" fill="none" stroke={CLAY} strokeWidth="15" data-id="element-570" data-mp="ro1z7st34viw" />
          {lanes.map(i => {
          const inset = -7.5 + i * 3.75;
          return <rect key={i} x={84 + inset} y={14 + inset} width={118 - inset * 2} height={78 - inset * 2} rx={39 - inset} fill="none" stroke={CHALK} strokeOpacity=".7" strokeWidth=".35" data-id="element-571" data-mp="1fkghke5rta9w" />;
        })}
          {/* finish line */}
          <path d="M128 5.5 V22.5" stroke={CHALK} strokeWidth="1.3" data-id="element-572" data-mp="1yhre3r00ppln" />
          {/* infield */}
          <rect x="91.5" y="21.5" width="103" height="63" rx="31.5" fill="#2E7D4F" fillOpacity=".55" data-id="element-573" data-mp="1kjt4t00k0jd0" />
          {/* athletes on the track */}
          {[[102, 9.8], [109, 13.6], [116, 9.8], [90.4, 30], [86.6, 40]].map(([x, y], i) => <g key={i} data-id="element-574" data-mp="e3qsqbfnoohu" data-mpi={i}>
              <circle cx={x + 0.3} cy={y + 0.6} r="1.7" fill="#000" fillOpacity=".3" data-id="element-575" data-mp="e48u1mh6dswm" />
              <circle cx={x} cy={y} r="1.6" fill={dotColor(i)} stroke={CHALK} strokeWidth=".35" data-id="element-576" data-mp="6hvc6jo68cb3" />
            </g>)}
          {/* the fence card, posted */}
          <g transform="translate(140 34) rotate(6)" data-id="element-577" data-mp="fkcyey3tt4y3">
            <rect x="0" y="0" width="15" height="19" rx="1.2" fill={CHALK} data-id="element-578" data-mp="fi0g2ju5oski" />
            <rect x="0" y="0" width="15" height="3" rx="1.2" fill={cardColor} data-id="element-579" data-mp="aljjd18w55vt" />
            <circle cx="7.5" cy="1.5" r=".55" fill={NAVY} data-id="element-580" data-mp="3j1v2qvke3ttr" />
            <path d="M2.5 6.5 H12.5 M2.5 9 H10.5 M2.5 11.5 H12 M2.5 14 H9" stroke={NAVY} strokeOpacity=".55" strokeWidth=".6" data-id="element-581" data-mp="3ez86migormqr" />
          </g>
          {/* cones */}
          <path d="M68 74 l1.4 2.4 h-2.8 z M74 74 l1.4 2.4 h-2.8 z" fill={HL} data-id="element-582" data-mp="3srxy32ezhjv9" />
        </g>

        {/* event-group band */}
        {isTeam ? GROUPS.map((g, i) => <rect key={g} x={i * 40} y="0" width="40" height="3.2" fill={g} data-id="element-583" data-mp="309ah9z70sqzz" data-mpi={i} />) : <rect x="0" y="0" width="160" height="3.2" fill={color} data-id="element-584" data-mp="2vc0tenza4ne9" />}

        {/* tab label, for covers stacked behind */}
        {peek && <text x="9" y="10.4" fill={CHALK} fontFamily={OSWALD} fontWeight="700" fontSize="5" letterSpacing=".3" data-id="element-585" data-mp="38vvatvp0440b">
            <tspan fill={HL} data-id="element-586" data-mp="3iw5o1gb1dbfh">VOL {number}</tspan> · {lines.join(' ').toUpperCase()}
          </text>}

        {/* brand */}
        <text x="9" y={peek ? 18 : 12.5} fill={CHALK} fontFamily={OSWALD} fontWeight="600" fontSize="4" letterSpacing=".5" data-id="element-587" data-mp="3nn42eacgrzg3">
          TRACK COACH READY
        </text>
        <rect x="9" y="16.2" width="21" height="5.4" rx="1" fill={HL} data-id="element-588" data-mp="i62iazlqdpc" />
        <text x="19.5" y="20" textAnchor="middle" fill={NAVY} fontFamily={OSWALD} fontWeight="700" fontSize="3.4" letterSpacing=".3" data-id="element-589" data-mp="gbl36njh3q0">
          VOLUME {number}
        </text>

        {/* title */}
        {lines.map((line, i) => <text key={line} x="8.4" y={36 + i * 13} fill={i === lines.length - 1 ? HL : CHALK} fontFamily={OSWALD} fontWeight="700" fontSize="13.5" letterSpacing=".1" data-id="element-590" data-mp="278vsbf8vrilc" data-mpi={i}>
            {line.toUpperCase()}
          </text>)}

        {/* badges */}
        {[{
        x: 9,
        w: 22,
        t: `${sessions} SESSIONS`
      }, {
        x: 33,
        w: 22,
        t: '60 · 75 · 90 MIN'
      }, {
        x: 57,
        w: 18,
        t: 'Y · MS · HS'
      }].map(b => <g key={b.t} data-id="element-591" data-mp="fmhcrhw2f2iy">
            <rect x={b.x} y="67" width={b.w} height="5.2" rx="1" fill="none" stroke={HL} strokeWidth=".45" data-id="element-592" data-mp="fj38g43ucj8v" />
            <text x={b.x + b.w / 2} y="70.6" textAnchor="middle" fill={HL} fontFamily="Inter, sans-serif" fontWeight="700" fontSize="2.3" data-id="element-593" data-mp="1c9432jqw5ty3">
              {b.t}
            </text>
          </g>)}

        {/* chalk band */}
        <rect x="0" y="82" width="160" height="8" fill={CHALK} data-id="element-594" data-mp="yc757yccvc7p" />
        <text x="80" y="87.1" textAnchor="middle" fill={NAVY} fontFamily="Inter, sans-serif" fontWeight="700" fontSize="2.5" letterSpacing=".3" data-id="element-595" data-mp="ye1pdafsz0aq">
          FENCE CARD SESSIONS  |  WORKSHEETS  |  LEVEL DIAL  |  FIELD CARD
        </text>
      </svg>
    </div>;
}